import { useMutation, useQuery, useSuspenseQuery } from "@apollo/client";
import {
  Alert,
  AssetCard,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CurrencyAmountUI,
  cn,
  DataTable,
  DataTableColumnHeader,
  DateUI,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  PrimaryFullScreenSpinner,
  StatusUI,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
} from "@repo/ui";
import {
  download,
  NotFoundError,
  neverFunc,
  prettyPrintEnum,
} from "@repo/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ColumnDef, Row } from "@tanstack/react-table";
import { Suspense, useState } from "react";
import { HiExternalLink } from "react-icons/hi";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { UpdatePaymentInstructionFileActions } from "~/components/PaymentInstructionFiles/Actions";
import { graphql } from "~/gql/client";
import type {
  StatementActionKind,
  StatementBatchStatementRowFragment,
  StatementKind,
} from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";
export const Route = createFileRoute(
  "/__auth/statement-batches/$kind_/$statementBatchId",
)({
  head: () => opsHead("Statement Batch"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function ButtonsCell({
  row,
}: {
  row: Row<StatementBatchStatementRowFragment>;
}) {
  const [getDownloadUrl, { loading: downloadLoading }] = useMutation(
    graphql(
      `mutation AdminDownloadStatement($input: DownloadStatementInput!) {
          downloadStatement(input: $input) {
            documentName
            documentUrl
          }
      }`,
    ),
  );

  const [viewOpen, setViewOpen] = useState(false);

  const [getViewUrl, { loading: viewLoading, data: viewData }] = useMutation(
    graphql(
      `mutation AdminViewStatement($input: DownloadStatementInput!) {
          downloadStatement(input: $input) {
            documentName
            documentUrl
          }
      }`,
    ),
  );

  const handleDownload = async () => {
    try {
      const { data } = await getDownloadUrl({
        variables: {
          input: {
            statementId: row.original.id,
          },
        },
      });
      if (!data || !data.downloadStatement.documentUrl) {
        toast({
          kind: "error",
          title: "Something went wrong preparing your statement",
          description: "Please try again later",
          autoHide: true,
        });
        return;
      }
      const { documentUrl, documentName } = data.downloadStatement;

      download(documentUrl, documentName);
    } catch (_err) {
      toast({
        kind: "error",
        title: "Something went wrong preparing your statement",
        description: "Please try again later",
        autoHide: true,
      });
    }
  };

  const handleView = () => {
    void getViewUrl({
      variables: {
        input: {
          statementId: row.original.id,
        },
      },
      onCompleted(data) {
        if (!data || !data.downloadStatement.documentUrl) {
          toast({
            kind: "error",
            title: "Something went wrong preparing your statement",
            description: "Please try again later",
            autoHide: true,
          });
          return;
        }
        setViewOpen(true);
      },
      onError() {
        toast({
          kind: "error",
          title: "Something went wrong preparing your statement",
          description: "Please try again later",
          autoHide: true,
        });
      },
    });
  };

  return (
    <div className="flex gap-2">
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <Button
          variant="outline"
          size="sm"
          disabled={row.original.status === "PROCESSING"}
          onClick={handleView}
          loading={viewLoading}
        >
          View
        </Button>
        <DialogContent className="flex h-[90dvh] flex-col sm:max-w-7xl">
          <DialogHeader>
            <DialogTitle>{`${
              row.original.organisation?.name
            } Monthly Statement #${row.original.id
              .slice(-4)
              .toUpperCase()}`}</DialogTitle>
          </DialogHeader>
          {viewData?.downloadStatement.documentUrl && (
            <iframe
              title={row.original.id}
              className="size-full"
              loading="lazy"
              src={viewData.downloadStatement.documentUrl}
            />
          )}
        </DialogContent>
      </Dialog>

      <Button
        variant={"outline"}
        disabled={row.original.status === "PROCESSING"}
        loading={downloadLoading}
        onClick={handleDownload}
        size="sm"
      >
        Download
      </Button>

      {row.original.organisation && (
        <>
          <QuickAction
            actionKind="REGENERATE"
            label="Regenerate"
            statementId={row.original.id}
            disabled={row.original.status === "PUBLISHED"}
          />
          <QuickAction
            actionKind="PUBLISH"
            label="Publish"
            statementId={row.original.id}
            disabled={row.original.status !== "STAFF_REVIEW_REQUIRED"}
          />
        </>
      )}
    </div>
  );
}

function getColumns(
  kind: StatementKind,
): ColumnDef<StatementBatchStatementRowFragment & { id: string }>[] {
  const organisationColumn: ColumnDef<StatementBatchStatementRowFragment> = {
    id: "organisation",
    accessorFn: (row) => row.organisation?.id ?? "",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Organisation" />
    ),
    cell: ({ row }) => {
      return (
        <div>
          {row.original.organisation?.name ?? row.original.organisation?.id}
        </div>
      );
    },
    sortingFn: (a, b) =>
      (a.original.organisation?.name ?? "").localeCompare(
        b.original.organisation?.name ?? "",
      ),
    filterFn: (row, _id, value: string[]) =>
      value.includes(row.original.organisation?.id ?? ""),
  };

  const statusColumn: ColumnDef<StatementBatchStatementRowFragment> = {
    id: "status",
    accessorFn: (row) => row.status,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      return row.original.status ? (
        <StatusUI status={row.original.status} />
      ) : (
        "-"
      );
    },
    sortingFn: (a, b) =>
      a.original.status && b.original.status
        ? a.original.status.localeCompare(b.original.status)
        : 0,
  };

  const assetColumn: ColumnDef<StatementBatchStatementRowFragment> = {
    id: "asset",
    accessorFn: (row) =>
      row.__typename === "AccountStatement" ? (row.asset?.id ?? "") : "",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Asset" />
    ),
    cell: ({ row }) => {
      if (row.original.__typename !== "AccountStatement") {
        return "-";
      }

      return row.original.asset ? (
        <AssetCard
          asset={row.original.asset}
          compactIcon
          subText={["PROVENANCE", "ACCOUNT_TYPE", "APY", "LIQUIDITY"]}
        />
      ) : (
        "-"
      );
    },
    enableSorting: false,
  };

  const buttonsColumn: ColumnDef<StatementBatchStatementRowFragment> = {
    id: "buttons",
    accessorFn: (row) => row.id,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Actions" />
    ),
    cell: ButtonsCell,
  };

  const invoiceColumn: ColumnDef<StatementBatchStatementRowFragment> = {
    id: "invoice",
    accessorFn: (row) =>
      row.__typename === "MonthlyStatement" ? row.invoice?.xeroId : null,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Invoice" />
    ),
    cell: ({ row }) => {
      if (
        row.original.__typename !== "MonthlyStatement" ||
        !row.original.invoice?.xeroId
      ) {
        return "-";
      }
      return (
        <Link
          to={`https://go.xero.com/app/!Fy3HT/invoicing/view/${row.original.invoice.xeroId}`}
          target="_blank"
        >
          <Button variant="ghost">
            <HiExternalLink className="size-5" />
          </Button>
        </Link>
      );
    },
    enableSorting: false,
  };

  const amountPayableColumn: ColumnDef<StatementBatchStatementRowFragment> = {
    id: "amountPayableColumn",
    accessorFn: (row) =>
      row.__typename === "MonthlyStatement"
        ? row.feesPayableExcludingGst?.amount
        : null,
    header: ({ column }) => (
      <DataTableColumnHeader
        align="right"
        column={column}
        title="Fee total (ex GST)"
      />
    ),
    cell: ({ row }) => {
      if (
        row.original.__typename !== "MonthlyStatement" ||
        !row.original.feesPayableExcludingGst
      ) {
        return "-";
      }
      return (
        <div className="w-full text-end">
          <CurrencyAmountUI
            amount={row.original.feesPayableExcludingGst.amount}
            currency={row.original.feesPayableExcludingGst.currency}
          />
        </div>
      );
    },
    enableSorting: false,
  };

  const serviceFeeColumn: ColumnDef<StatementBatchStatementRowFragment> = {
    id: "serviceFeeColumn",
    accessorFn: (row) =>
      row.__typename === "MonthlyStatement"
        ? row.serviceFeesExGst?.amount
        : null,
    header: ({ column }) => (
      <DataTableColumnHeader
        align="right"
        column={column}
        title="Service Fees"
      />
    ),
    cell: ({ row }) => {
      if (
        row.original.__typename !== "MonthlyStatement" ||
        !row.original.serviceFeesExGst
      ) {
        return "-";
      }
      return (
        <div className="w-full text-end">
          <CurrencyAmountUI
            amount={row.original.serviceFeesExGst.amount}
            currency={row.original.serviceFeesExGst.currency}
          />
        </div>
      );
    },
    enableSorting: false,
  };

  const licenceFeeColumn: ColumnDef<StatementBatchStatementRowFragment> = {
    id: "licenceFeeColumn",
    accessorFn: (row) =>
      row.__typename === "MonthlyStatement"
        ? row.licenseFeeExGst?.amount
        : null,
    header: ({ column }) => (
      <DataTableColumnHeader
        align="right"
        column={column}
        title="Licence Fee"
      />
    ),
    cell: ({ row }) => {
      if (
        row.original.__typename !== "MonthlyStatement" ||
        !row.original.licenseFeeExGst
      ) {
        return "-";
      }
      return (
        <div className="w-full text-end">
          <CurrencyAmountUI
            amount={row.original.licenseFeeExGst.amount}
            currency={row.original.licenseFeeExGst.currency}
          />
        </div>
      );
    },
    enableSorting: false,
  };

  const availablePaymentMethodColumn: ColumnDef<StatementBatchStatementRowFragment> =
    {
      id: "availablePaymentMethod",
      accessorFn: (row) =>
        row.__typename === "MonthlyStatement"
          ? row.availableFeePaymentMethod
          : null,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Payment method" />
      ),
      cell: ({ row }) => {
        if (row.original.__typename !== "MonthlyStatement") {
          return null;
        }

        switch (row.original.availableFeePaymentMethod) {
          case "INVOICE":
            return "Invoice";
          case "DIRECT_DEBIT":
            return "Direct debit";
          case undefined:
          case null:
            return "-";
          default:
            return neverFunc(row.original.availableFeePaymentMethod);
        }
      },
      enableSorting: false,
    };

  switch (kind) {
    case "MONTHLY":
      return [
        organisationColumn,
        serviceFeeColumn,
        licenceFeeColumn,
        amountPayableColumn,
        availablePaymentMethodColumn,
        invoiceColumn,
        statusColumn,
        buttonsColumn,
      ];
    case "ACTIVITY":
    case "BALANCE":
      return [organisationColumn, statusColumn, buttonsColumn];
    case "ACCOUNT":
      return [assetColumn, statusColumn, buttonsColumn];
    default:
      throw neverFunc(kind);
  }
}

const pageQueryDocument = graphql(`
  query AdminStatementBatchPage($statementBatchId: ID!) {
      node(id: $statementBatchId) {
          ...AdminStatementBatchPage
      }
  }

  fragment AdminStatementBatchPage on StatementBatch {
      id
      shortRef
      periodStartAtInclusive
      periodEndAtExclusive
      statements {
          ...StatementBatchStatementRow
      }
      organisationsMissingXeroContacts {
          id
          name
      }
      pendingPaymentInstructionFile {
          id
      }
  }

  fragment StatementBatchStatementRow on Statement {
    id
    status
    organisation {
      id
      name
    }
    ...on AccountStatement {
        asset {
            ...AssetCardFields
        }
    }
    ...on MonthlyStatement {
        invoice {
            id
            xeroId
        }
        feesPayableExcludingGst {
            currency
            amount
        }
        availableFeePaymentMethod
        serviceFeesExGst {
            currency
            amount
        }
        licenseFeeExGst {
            currency
            amount
        }
    }
  }
`);

function View() {
  const params = Route.useParams();

  const kind = statementQueryParamToKind(params.kind);
  if (!kind) {
    throw new NotFoundError();
  }

  const { data } = useSuspenseQuery(pageQueryDocument, {
    variables: {
      statementBatchId: params.statementBatchId ?? "",
    },
  });

  const statementBatch = data.node;
  if (!statementBatch || statementBatch.__typename !== "StatementBatch") {
    throw new NotFoundError();
  }

  useOpsTitle(
    `${prettyPrintEnum(kind)} Statement Batch #${statementBatch.shortRef}`,
  );

  const statements = statementBatch.statements ?? [];
  const processingStatementsCount = statements.reduce<number>(
    (acc, curr) => (curr.status === "PROCESSING" ? acc + 1 : acc),
    0,
  );

  const monthlyStatements =
    statementBatch.statements?.filter(
      (s) => s.__typename === "MonthlyStatement",
    ) ?? [];
  const orgsMissingXeroContacts =
    statementBatch.organisationsMissingXeroContacts ?? [];

  const invoicingStatements = monthlyStatements.filter(
    (s) => s.availableFeePaymentMethod === "INVOICE",
  );
  const directDebitStatements = monthlyStatements.filter(
    (s) => s.availableFeePaymentMethod === "DIRECT_DEBIT",
  );

  const canGenerateInvoices =
    invoicingStatements.length > 0 && orgsMissingXeroContacts.length === 0;
  const canProcessDirectDebits =
    directDebitStatements.length > 0 && orgsMissingXeroContacts.length === 0;

  const [mutation, { loading: mutationLoading }] = useMutation(
    graphql(`
        mutation ProcessStatementBatchDirectDebitFees($input: ProcessStatementBatchFeeDirectDebitsInput!) {
            processStatementBatchFeeDirectDebits(input: $input) {
                macquarieCmaEgressPaymentInstructionFile {
                    id
                    url
                }
                macquarieCmaAcceleratorEgressPaymentInstructionFile {
                    id
                    url
                }
                statementBatch {
                    ...AdminStatementBatchPage
                }
            }
        }
    `),
    {
      onCompleted(data) {
        toast({ kind: "success", autoHide: true, title: "Generated" });

        const cmaUrl =
          data.processStatementBatchFeeDirectDebits
            ?.macquarieCmaEgressPaymentInstructionFile?.url;
        if (cmaUrl) {
          download(cmaUrl);
        }

        const acceleratorUrl =
          data.processStatementBatchFeeDirectDebits
            ?.macquarieCmaAcceleratorEgressPaymentInstructionFile?.url;
        if (acceleratorUrl) {
          download(acceleratorUrl);
        }
      },
      onError(error) {
        const errorMessage =
          error.graphQLErrors?.[0]?.message ||
          "Failed to create payment instruction file";

        toast({
          kind: "error",
          autoHide: false,
          title: "Error creating payment instruction file",
          description: errorMessage,
        });
      },
    },
  );

  const [postInvoiceBatch, { loading: postInvoiceBatchLoading }] = useMutation(
    graphql(`
    mutation PostInvoiceStatementBatch($input: PostInvoiceStatementBatchInput!) {
        postInvoiceStatementBatch(input: $input) {
            statementBatch {
                ...AdminStatementBatchPage
            }
        }
    }
  `),
    {
      onCompleted() {
        toast({ kind: "success", autoHide: true, title: "Invoices generated" });
      },
      onError(error) {
        const errorMessage = error.graphQLErrors?.[0]?.message;

        toast({
          kind: "error",
          autoHide: false,
          title: "Error generating invoices",
          description: errorMessage,
        });
      },
    },
  );

  const [regenerateAllStatements, { loading: regenerateAllStatementsLoading }] =
    useMutation(
      graphql(`
        mutation RegenerateAllStatements($input: RegenerateStatementBatchInput!) {
            regenerateStatementBatch(input: $input) {
                statementBatch {
                    ...AdminStatementBatchPage
                }
            }
        }
    `),
      {
        onCompleted() {
          toast({
            kind: "success",
            autoHide: true,
            title: "Statements regenerated",
          });
        },
        onError(error) {
          const errorMessage = error.graphQLErrors?.[0]?.message;

          toast({
            kind: "error",
            autoHide: false,
            title: "Error generating statements",
            description: errorMessage,
          });
        },
      },
    );

  const [publishAllStatements, { loading: publishAllStatementsLoading }] =
    useMutation(
      graphql(`
        mutation PublishAllStatements($input: PublishStatementBatchInput!) {
            publishStatementBatch(input: $input) {
                statementBatch {
                    ...AdminStatementBatchPage
                }
            }
        }
    `),
      {
        onCompleted() {
          toast({
            kind: "success",
            autoHide: true,
            title: "Statements published",
          });
        },
        onError(error) {
          const errorMessage = error.graphQLErrors?.[0]?.message;

          toast({
            kind: "error",
            autoHide: false,
            title: "Error publishing invoices",
            description: errorMessage,
          });
        },
      },
    );

  useQuery(pageQueryDocument, {
    skip: processingStatementsCount === 0,
    pollInterval: 5000,
    variables: {
      statementBatchId: statementBatch.id,
    },
  });

  return (
    <AdminPageContainer testId={`admin-${kind}-statements-batch`}>
      <AdminPageHeader
        title={`${prettyPrintEnum(kind)} Statement Batch #${statementBatch.shortRef}`}
        subtitle={statementBatch.id}
      />

      <div className="flex flex-col gap-8">
        {processingStatementsCount > 0 && (
          <div className="flex flex-row">
            <Alert
              className="mt-4"
              kind="warning"
              title="Statements generating"
              description="One or more statements in this batch are still generating. They
                should be ready soon; check back in a few minutes.

                This page will be updated automatically."
            />
          </div>
        )}
        <div className="flex flex-row gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm text-tertiary">
                Statement period
              </CardTitle>
              <CardContent className="p-0">
                {kind === "BALANCE" ? (
                  <>
                    {statementBatch.periodEndAtExclusive ? (
                      <DateUI date={statementBatch.periodEndAtExclusive} />
                    ) : (
                      <div>N/A</div>
                    )}
                  </>
                ) : (
                  <>
                    {statementBatch.periodStartAtInclusive != null &&
                    statementBatch.periodEndAtExclusive != null ? (
                      <div className="flex flex-row gap-1">
                        <DateUI date={statementBatch.periodStartAtInclusive} />
                        <span>&nbsp;-&nbsp;</span>
                        <DateUI date={statementBatch.periodEndAtExclusive} />
                      </div>
                    ) : (
                      <div>N/A</div>
                    )}
                  </>
                )}
              </CardContent>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-sm text-tertiary">
                Statements in batch
              </CardTitle>
              <CardContent className="p-0">{statements.length}</CardContent>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-sm text-tertiary">
                Statements published
              </CardTitle>
              <CardContent className="p-0">
                {
                  statements.filter(
                    (statement) => statement.status === "PUBLISHED",
                  ).length
                }
              </CardContent>
            </CardHeader>
          </Card>
        </div>
        <div className="flex flex-row items-center gap-4">
          <Button
            type="submit"
            variant="outline"
            disabled={statements.some((s) => s.status === "PUBLISHED")}
            loading={regenerateAllStatementsLoading}
            onClick={() =>
              regenerateAllStatements({
                variables: {
                  input: { statementBatchId: statementBatch.id, kind },
                },
              })
            }
          >
            Regenerate all statements
          </Button>
          <Button
            type="submit"
            variant="outline"
            disabled={statements.some((s) => s.status === "PUBLISHED")}
            loading={publishAllStatementsLoading}
            onClick={() =>
              publishAllStatements({
                variables: {
                  input: { statementBatchId: statementBatch.id, kind },
                },
              })
            }
          >
            Publish all statements
          </Button>
          {kind === "MONTHLY" && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-4">
                  <Button
                    type="submit"
                    variant="accent"
                    disabled={!canGenerateInvoices}
                    loading={postInvoiceBatchLoading}
                    onClick={() =>
                      postInvoiceBatch({
                        variables: {
                          input: { statementBatchId: statementBatch.id },
                        },
                      })
                    }
                  >
                    Generate Invoices
                    {invoicingStatements.length > 0
                      ? ` (${invoicingStatements.length})`
                      : ""}
                  </Button>

                  <div
                    className={cn(
                      statementBatch.pendingPaymentInstructionFile &&
                        "bg-secondary",
                      "flex h-16 items-center gap-4 rounded-lg p-2",
                    )}
                  >
                    <Button
                      type="submit"
                      variant="accent"
                      disabled={!canProcessDirectDebits || mutationLoading}
                      loading={mutationLoading}
                      onClick={() => {
                        mutation({
                          variables: {
                            input: {
                              statementBatchId: statementBatch.id,
                            },
                          },
                        });
                      }}
                    >
                      Download direct debit file/s{" "}
                      {directDebitStatements.length > 0
                        ? ` (${directDebitStatements.length})`
                        : ""}
                    </Button>

                    {statementBatch.pendingPaymentInstructionFile && (
                      <UpdatePaymentInstructionFileActions
                        id={statementBatch.pendingPaymentInstructionFile.id}
                      />
                    )}
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent hidden={orgsMissingXeroContacts.length === 0}>
                <div className="flex max-h-64 w-96 flex-col gap-2 overflow-y-auto">
                  {orgsMissingXeroContacts.length > 0 && (
                    <div>
                      <p>
                        The following organisations are missing a xero contact
                        ID:
                      </p>
                      <div className="flex flex-col gap-1">
                        {orgsMissingXeroContacts.map((o) => (
                          <Link
                            to="/organisations/$organisationId"
                            params={{ organisationId: o?.id ?? "" }}
                            key={o?.id}
                          >
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-white"
                            >
                              - {o?.name}
                            </Button>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <DataTable
          data={statements}
          columns={getColumns(kind)}
          columnSortProps={{
            id: "organisation",
            desc: false,
          }}
        />
      </div>
    </AdminPageContainer>
  );
}

function statementQueryParamToKind(s?: string): StatementKind | null {
  switch (s) {
    case "monthly":
      return "MONTHLY";
    case "activity":
      return "ACTIVITY";
    case "balance":
      return "BALANCE";
    case "account":
      return "ACCOUNT";
    default:
      return null;
  }
}

const QuickAction: React.FC<{
  actionKind: StatementActionKind;
  disabled: boolean;
  label: string;
  statementId: string;
}> = ({ actionKind, disabled, label, statementId }) => {
  const [actionStatementMutation, { loading }] = useMutation(
    graphql(`
      mutation StatementQuickAction($input: ActionStatementInput!) {
        actionStatement(input: $input) {
          statement {
            id
            status
          }
        }
      }
    `),
  );

  return (
    <Button
      type="button"
      variant="outline"
      disabled={disabled || loading}
      size="sm"
      onClick={async (e) => {
        e.preventDefault();
        const defaultErrorMessage =
          "There was an error submitting your form. Please try again later.";
        await actionStatementMutation({
          variables: {
            input: {
              action: actionKind,
              statementId,
            },
          },
          onCompleted: () => {
            toast({
              kind: "success",
              autoHide: true,
              title: "Statement updated",
            });
          },
          onError: (error) => {
            const description =
              error.graphQLErrors?.[0]?.message ?? defaultErrorMessage;
            toast({
              kind: "error",
              autoHide: false,
              title: "Failed to update statement",
              description,
            });
            throw error;
          },
        });
      }}
    >
      {label}
    </Button>
  );
};
